/**
 * ArtifactQuickGenModal - 从动作卡片快速生成输出件的弹窗
 * 用于建议动作与输出件的显式联动
 */
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, 
  FileText, 
  Triangle, 
  Map,
  Download,
  Copy,
  Check,
  Loader2
} from 'lucide-react'
import { clsx } from 'clsx'
import api from '@/services/api'
import { ArtifactType, ARTIFACT_TYPE_NAMES } from '@/types'

interface ArtifactQuickGenModalProps {
  isOpen: boolean
  onClose: () => void
  dealId: string
  dealName: string
  artifactType: ArtifactType
  actionContext?: string  // 触发生成的动作内容，用于上下文提示
}

// 输出件配置
const ARTIFACT_CONFIG: Record<ArtifactType, {
  icon: React.ElementType
  color: string
  bgColor: string
  description: string
  needsInput?: boolean
}> = {
  one_pager: {
    icon: FileText,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    description: '用EB语言阐述业务价值、ROI、窗口期压力',
  },
  decision_triangle: {
    icon: Triangle,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    description: 'ROI/窗口期/机会成本三维度话术模板',
  },
  visit_canvas: {
    icon: Map,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    description: '拜访准备清单、问题设计、目标设定',
    needsInput: true,
  },
}

export default function ArtifactQuickGenModal({
  isOpen,
  onClose,
  dealId,
  dealName,
  artifactType,
  actionContext,
}: ArtifactQuickGenModalProps) {
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedContent, setGeneratedContent] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // 拜访画布额外输入
  const [visitTarget, setVisitTarget] = useState('')
  const [visitPurpose, setVisitPurpose] = useState('')

  const config = ARTIFACT_CONFIG[artifactType]
  const Icon = config?.icon || FileText

  // 重置状态
  useEffect(() => {
    if (isOpen) {
      setGeneratedContent(null)
      setError(null)
      setCopied(false)
      setVisitTarget('')
      setVisitPurpose('')
    }
  }, [isOpen, artifactType])

  const handleGenerate = async () => {
    setIsGenerating(true)
    setError(null)
    setGeneratedContent(null)
    
    try {
      let response
      
      switch (artifactType) {
        case 'one_pager':
          response = await api.post(`/artifacts/deal/${dealId}/one-pager`)
          break
        case 'decision_triangle':
          response = await api.post(`/artifacts/deal/${dealId}/decision-triangle`)
          break
        case 'visit_canvas':
          if (!visitTarget || !visitPurpose) {
            setError('请填写拜访对象和拜访目的')
            setIsGenerating(false)
            return
          }
          response = await api.post(`/artifacts/deal/${dealId}/visit-canvas`, {
            visit_target: visitTarget,
            visit_purpose: visitPurpose,
          })
          break
      }
      
      if (response?.data?.markdownContent) {
        setGeneratedContent(response.data.markdownContent)
      } else if (response?.data?.error) {
        setError(response.data.error)
      }
    } catch (err: any) {
      setError(err.message || '生成失败，请重试')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCopy = async () => {
    if (generatedContent) {
      await navigator.clipboard.writeText(generatedContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleDownload = () => {
    if (generatedContent) {
      const blob = new Blob([generatedContent], { type: 'text/markdown' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${dealName}_${artifactType}_${new Date().toISOString().split('T')[0]}.md`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
          />
          
          {/* 弹窗内容 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 头部 */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className={clsx('p-2 rounded-xl', config?.bgColor)}>
                    <Icon className={clsx('w-5 h-5', config?.color)} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      生成{ARTIFACT_TYPE_NAMES[artifactType]}
                    </h2>
                    <p className="text-sm text-gray-500">{dealName}</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* 内容区 */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                {/* 上下文提示 */}
                {actionContext && !generatedContent && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">关联动作：</span>{actionContext}
                    </p>
                  </div>
                )}

                {/* 输出件描述 */}
                {!generatedContent && (
                  <div className={clsx('p-4 rounded-xl mb-6', config?.bgColor)}>
                    <p className="text-sm text-gray-700">{config?.description}</p>
                  </div>
                )}

                {/* 拜访画布额外输入 */}
                {artifactType === 'visit_canvas' && !generatedContent && (
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        拜访对象 *
                      </label>
                      <input
                        type="text"
                        value={visitTarget}
                        onChange={(e) => setVisitTarget(e.target.value)}
                        placeholder="例如：张总（IT部门负责人）"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        拜访目的 *
                      </label>
                      <input
                        type="text"
                        value={visitPurpose}
                        onChange={(e) => setVisitPurpose(e.target.value)}
                        placeholder="例如：确认技术评估标准"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                )}

                {/* 错误提示 */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {error}
                  </div>
                )}

                {/* 生成按钮 */}
                {!generatedContent && (
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className={clsx(
                      'w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors',
                      isGenerating
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    )}
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        正在生成...
                      </>
                    ) : (
                      <>
                        <Icon className="w-5 h-5" />
                        立即生成
                      </>
                    )}
                  </button>
                )}

                {/* 生成结果展示 */}
                {generatedContent && (
                  <div>
                    {/* 操作栏 */}
                    <div className="flex items-center justify-end gap-2 mb-4">
                      <button
                        onClick={handleCopy}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4 text-green-500" />
                            <span className="text-green-600">已复制</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            复制
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleDownload}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-100 text-purple-700 hover:bg-purple-200 rounded-lg transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        下载
                      </button>
                    </div>

                    {/* Markdown 内容预览 */}
                    <div className="max-h-[50vh] overflow-y-auto p-4 bg-gray-50 rounded-xl">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono leading-relaxed">
                        {generatedContent}
                      </pre>
                    </div>

                    {/* 重新生成按钮 */}
                    <button
                      onClick={() => setGeneratedContent(null)}
                      className="w-full mt-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      重新生成
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
